import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import useLocalStorage from "use-local-storage";
import { motion, AnimatePresence } from "framer-motion";
import {
  Typography,
  Box,
  IconButton,
  Chip,
  Avatar,
  LinearProgress,
  Paper,
  TextField,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  VideoLibrary as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  InsertDriveFile as FileIcon,
  MoreVert as MoreVertIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useAtom } from "jotai";
import { themeAtom } from "../../../-Atoms";
import { db, storage } from "../../../firebaseConfig";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import convertHeicToJpg from "../../../Utils/convertHeicToJpg";

const AttachmentsV2 = forwardRef(({
  jobData,
  clientData,
  setUrlModalUrl,
  setURLModalOpen,
}, ref) => {
  const [theme] = useAtom(themeAtom);
  const [jobAttachments, setJobAttachments] = useState([]);
  const [clientAttachments, setClientAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Compact/Full view toggle with localStorage
  const [isCompactView, setIsCompactView] = useLocalStorage("attachments-compact-view", false);

  useEffect(() => {
    const tmpJobAttachments = [];
    const tmpClientAttachments = [];
    
    if (jobData?.attachments) {
      jobData.attachments.forEach((attachment) => {
        tmpJobAttachments.push(attachment);
      });
    }
    
    if (clientData?.attachments) {
      clientData.attachments.forEach((attachment) => {
        tmpClientAttachments.push(attachment);
      });
    }
    
    setJobAttachments([...tmpJobAttachments]);
    setClientAttachments([...tmpClientAttachments]);
  }, [jobData, clientData]);

  // Drag and drop handlers
  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter(prev => prev + 1);
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragOver(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter(prev => prev - 1);
      if (dragCounter <= 1) {
        setIsDragOver(false);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDragCounter(0);
      
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        await handleFileUpload(files);
      }
    };

    const dropZone = dropZoneRef.current;
    if (dropZone) {
      dropZone.addEventListener("dragenter", handleDragEnter);
      dropZone.addEventListener("dragleave", handleDragLeave);
      dropZone.addEventListener("dragover", handleDragOver);
      dropZone.addEventListener("drop", handleDrop);
    }

    return () => {
      if (dropZone) {
        dropZone.removeEventListener("dragenter", handleDragEnter);
        dropZone.removeEventListener("dragleave", handleDragLeave);
        dropZone.removeEventListener("dragover", handleDragOver);
        dropZone.removeEventListener("drop", handleDrop);
      }
    };
  }, [dragCounter]);

  // Expose global file upload handler to parent
  useImperativeHandle(ref, () => ({
    handleGlobalFileUpload: handleFileUpload
  }));

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    let newAttachments = [];

    try {
      for (let i = 0; i < files.length; i++) {
        let file = await convertHeicToJpg(files[i]);
        let fileName = file.name;

        // Add random suffix to prevent filename conflicts
        const random = uuidv4().slice(0, 5);
        const ext = fileName.match(/(\.[^.]+)$/)?.[1] || "";
        const fnOrig = fileName.replace(ext, "");
        const newFn = `${fnOrig}_${random}${ext}`;
        fileName = newFn;

        if (file.size > 150000000) {
          alert("File must be under 150MB");
          continue;
        }

        // Upload to Firebase Storage
        const fileStorageRef = storageRef(storage, `JobAttachments/${jobData.id}/${fileName}`);
        await uploadBytes(fileStorageRef, file);
        const url = await getDownloadURL(fileStorageRef);

        newAttachments.push({ 
          fileName: fileName, 
          url: url,
          uploadedAt: new Date().toISOString(),
          size: file.size,
          type: file.type,
        });

        // Update progress
        setUploadProgress(((i + 1) / files.length) * 100);
      }

      // Update Firestore with new attachments
      if (newAttachments.length > 0) {
        for (const attachment of newAttachments) {
          await updateDoc(doc(db, "JobData", jobData.id), {
            attachments: arrayUnion(attachment),
          });
        }
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Error uploading files. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleAttachment = async (e) => {
    const files = Array.from(e.target.files);
    await handleFileUpload(files);
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.match(/\.([^.]+)$/)?.[1]?.toLowerCase() || "";
    const imageExts = ["jpg", "png", "jpeg", "gif", "webp", "heic", "bmp", "svg"];
    const videoExts = ["mp4", "avi", "mov", "wmv", "flv", "webm"];
    const audioExts = ["mp3", "wav", "flac", "aac", "ogg"];
    const archiveExts = ["zip", "rar", "7z", "tar", "gz"];
    
    if (imageExts.includes(ext)) return <ImageIcon fontSize="small" />;
    if (videoExts.includes(ext)) return <VideoIcon fontSize="small" />;
    if (audioExts.includes(ext)) return <AudioIcon fontSize="small" />;
    if (archiveExts.includes(ext)) return <ArchiveIcon fontSize="small" />;
    if (ext === "pdf") return <DocumentIcon fontSize="small" />;
    return <FileIcon fontSize="small" />;
  };

  const totalAttachments = jobAttachments.length + clientAttachments.length;
  const allAttachments = [...clientAttachments, ...jobAttachments];

  const toggleCompactView = () => {
    setIsCompactView(!isCompactView);
  };

  return (
    <Box
      ref={dropZoneRef}
      sx={{
        position: "relative",
        width: "100%",
        mt: 2,
        transition: "all 0.3s ease",
      }}
    >
      {/* Drag and Drop Overlay */}
      <AnimatePresence>
        {isDragOver && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              background: theme === "dark" 
                ? "rgba(144, 202, 249, 0.1)" 
                : "rgba(25, 118, 210, 0.1)",
              border: `2px dashed ${theme === "dark" ? "#90caf9" : "#1976d2"}`,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(4px)",
            }}
          >
            <Box sx={{ textAlign: "center", color: theme === "dark" ? "#90caf9" : "#1976d2" }}>
              <CloudUploadIcon sx={{ fontSize: isCompactView ? 32 : 48, mb: 1 }} />
              <Typography variant={isCompactView ? "body1" : "h6"} sx={{ fontWeight: 600 }}>
                Drop files to upload
              </Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: isCompactView ? 1 : 2,
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 2,
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography 
              variant="subtitle1" 
              onClick={toggleCompactView}
              sx={{ 
                fontWeight: 500,
                color: theme === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.9)",
                cursor: "pointer",
                userSelect: "none",
                padding: "4px 8px",
                borderRadius: 1,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  background: theme === "dark" 
                    ? "rgba(255,255,255,0.05)" 
                    : "rgba(0,0,0,0.05)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Attachments
            </Typography>
            {totalAttachments > 0 && (
              <Chip
                label={totalAttachments}
                size="small"
                sx={{
                  background: theme === "dark" 
                    ? "rgba(100,100,255,0.2)" 
                    : "rgba(100,100,255,0.1)",
                  color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)",
                  height: 20,
                  fontSize: "0.75rem",
                }}
              />
            )}
          </Box>
          
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            size="small"
            sx={{
              bgcolor: theme === "dark" ? "rgba(60,60,60,0.8)" : "rgba(240,240,240,0.8)",
              color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)",
              boxShadow: theme === "dark" ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.1)",
              "&:hover": {
                bgcolor: theme === "dark" ? "rgba(70,70,70,0.9)" : "rgba(245,245,245,0.9)",
              },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Upload Progress */}
        <AnimatePresence>
          {isUploading && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress}
                sx={{
                  borderRadius: 1,
                  height: 6,
                  backgroundColor: theme === "dark" 
                    ? "rgba(255, 255, 255, 0.1)" 
                    : "rgba(0, 0, 0, 0.1)",
                }}
              />
              <Typography variant="caption" sx={{ mt: 1, display: "block", textAlign: "center" }}>
                Uploading... {Math.round(uploadProgress)}%
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Attachments Display */}
        {totalAttachments > 0 ? (
          isCompactView ? (
            // Compact grid view
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {allAttachments.slice(0, 6).map((attachment, index) => (
                <motion.div
                  key={`${attachment.url}_${index}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1,
                      cursor: "pointer",
                      background: theme === "dark" 
                        ? "rgba(255, 255, 255, 0.05)" 
                        : "rgba(0, 0, 0, 0.03)",
                      border: theme === "dark" 
                        ? "1px solid rgba(255, 255, 255, 0.1)" 
                        : "1px solid rgba(0, 0, 0, 0.1)",
                      borderRadius: 2,
                      minWidth: 80,
                      textAlign: "center",
                      "&:hover": {
                        background: theme === "dark" 
                          ? "rgba(255, 255, 255, 0.08)" 
                          : "rgba(0, 0, 0, 0.05)",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => {
                      setUrlModalUrl(attachment.url);
                      setURLModalOpen(true);
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                      <Avatar
                        sx={{
                          background: "transparent",
                          color: theme === "dark" ? "#90caf9" : "#1976d2",
                          width: 24,
                          height: 24,
                        }}
                      >
                        {getFileIcon(attachment.fileName)}
                      </Avatar>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: "0.65rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 70,
                          display: "block",
                        }}
                      >
                        {attachment.fileName.length > 12 
                          ? `${attachment.fileName.substring(0, 9)}...` 
                          : attachment.fileName}
                      </Typography>
                      {clientAttachments.includes(attachment) && (
                        <Chip
                          label="Client"
                          size="small"
                          variant="outlined"
                          sx={{ 
                            height: 16, 
                            fontSize: "0.6rem",
                            "& .MuiChip-label": { px: 0.5 },
                            color: theme === "dark" ? "#81c784" : "#388e3c",
                            borderColor: theme === "dark" ? "#81c784" : "#388e3c",
                          }}
                        />
                      )}
                    </Box>
                  </Paper>
                </motion.div>
              ))}
              
              {totalAttachments > 6 && (
                <Paper
                  elevation={1}
                  sx={{
                    p: 1,
                    background: theme === "dark" 
                      ? "rgba(255, 255, 255, 0.03)" 
                      : "rgba(0, 0, 0, 0.02)",
                    border: theme === "dark" 
                      ? "1px dashed rgba(255, 255, 255, 0.2)" 
                      : "1px dashed rgba(0, 0, 0, 0.2)",
                    borderRadius: 2,
                    minWidth: 80,
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    +{totalAttachments - 6} more
                  </Typography>
                </Paper>
              )}
            </Box>
          ) : (
            // Full list view
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {/* Client Attachments */}
              {clientAttachments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary", fontWeight: 500 }}>
                    Client Files
                  </Typography>
                  {clientAttachments.map((attachment, index) => (
                    <AttachmentItem
                      key={`client_${attachment.url}_${index}`}
                      attachment={attachment}
                      index={index}
                      kind="client"
                      jobData={jobData}
                      setUrlModalUrl={setUrlModalUrl}
                      setURLModalOpen={setURLModalOpen}
                      theme={theme}
                      readonly={true}
                    />
                  ))}
                </Box>
              )}

              {/* Job Attachments */}
              {jobAttachments.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary", fontWeight: 500 }}>
                    Job Files
                  </Typography>
                  {jobAttachments.map((attachment, index) => (
                    <AttachmentItem
                      key={`job_${attachment.url}_${index}`}
                      attachment={attachment}
                      index={index}
                      kind="job"
                      jobData={jobData}
                      setUrlModalUrl={setUrlModalUrl}
                      setURLModalOpen={setURLModalOpen}
                      theme={theme}
                      readonly={false}
                      attachments={jobAttachments}
                      setAttachments={setJobAttachments}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: isCompactView ? 1 : 2,
              color: theme === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
              Click + or drag files here
            </Typography>
          </Box>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          multiple
          style={{ display: "none" }}
          onChange={handleAttachment}
        />
      </Box>
    </Box>
  );
});

const AttachmentItem = ({
  attachment,
  index,
  kind,
  jobData,
  setUrlModalUrl,
  setURLModalOpen,
  theme,
  readonly,
  attachments,
  setAttachments,
}) => {
  const [ext, setExt] = useState("");
  const [fileNameNoExt, setFileNameNoExt] = useState("");
  const [showImage, setShowImage] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [newFileName, setNewFileName] = useState("");

  const processedUrl = (attachment.url || "").includes("dropbox.com")
    ? (attachment.url || "").replace("dl=0", "raw=1")
    : (attachment.url || "");

  useEffect(() => {
    const tmpExt = attachment.fileName.match(/\.([^.]+)$/)?.[1]?.toLowerCase() || "";
    setExt(tmpExt);
    setFileNameNoExt(attachment.fileName.replace(`.${tmpExt}`, ""));
    setNewFileName(attachment.fileName.replace(`.${tmpExt}`, ""));
    
    const imageExts = ["jpg", "png", "jpeg", "gif", "webp", "heic", "bmp", "svg"];
    setShowImage(imageExts.includes(tmpExt));
  }, [attachment]);

  const getFileIcon = () => {
    const imageExts = ["jpg", "png", "jpeg", "gif", "webp", "heic", "bmp", "svg"];
    const videoExts = ["mp4", "avi", "mov", "wmv", "flv", "webm"];
    const audioExts = ["mp3", "wav", "flac", "aac", "ogg"];
    const archiveExts = ["zip", "rar", "7z", "tar", "gz"];
    
    if (imageExts.includes(ext)) return <ImageIcon />;
    if (videoExts.includes(ext)) return <VideoIcon />;
    if (audioExts.includes(ext)) return <AudioIcon />;
    if (archiveExts.includes(ext)) return <ArchiveIcon />;
    if (ext === "pdf") return <DocumentIcon />;
    return <FileIcon />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = () => {
    setEditMode(true);
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (kind !== "job" || !attachments || !setAttachments) return;
    
    try {
      const updatedAttachments = attachments.filter((_, i) => i !== index);
      setAttachments(updatedAttachments);
      
      await updateDoc(doc(db, "JobData", jobData.id), { 
        attachments: updatedAttachments 
      });
    } catch (error) {
      console.error("Error deleting attachment:", error);
    }
    
    handleMenuClose();
  };

  const handleSaveEdit = async () => {
    if (kind !== "job" || !attachments || !setAttachments) return;
    
    try {
      const updatedFileName = `${newFileName}.${ext}`;
      const updatedAttachments = [...attachments];
      updatedAttachments[index] = { ...attachment, fileName: updatedFileName };
      
      setAttachments(updatedAttachments);
      
      await updateDoc(doc(db, "JobData", jobData.id), { 
        attachments: updatedAttachments 
      });
      
      setEditMode(false);
    } catch (error) {
      console.error("Error updating attachment:", error);
    }
  };

  const handleClick = () => {
    setUrlModalUrl(attachment.url);
    setURLModalOpen(true);
  };

  const handleOpenNew = (event) => {
    event.stopPropagation();
    window.open(attachment.url, "_blank");
  };

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        style={{ marginBottom: 8 }}
      >
        <Paper
          elevation={1}
          sx={{
            p: 2,
            cursor: "pointer",
            background: theme === "dark" 
              ? "rgba(255, 255, 255, 0.03)" 
              : "rgba(0, 0, 0, 0.02)",
            border: theme === "dark" 
              ? "1px solid rgba(255, 255, 255, 0.05)" 
              : "1px solid rgba(0, 0, 0, 0.05)",
            borderRadius: 2,
            "&:hover": {
              background: theme === "dark" 
                ? "rgba(255, 255, 255, 0.05)" 
                : "rgba(0, 0, 0, 0.03)",
              transform: "translateY(-1px)",
            },
            transition: "all 0.2s ease-in-out",
          }}
          onClick={handleClick}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* File preview/icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                background: theme === "dark" 
                  ? "rgba(255, 255, 255, 0.1)" 
                  : "rgba(0, 0, 0, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {showImage ? (
                <img
                  src={processedUrl}
                  style={{ 
                    width: "100%", 
                    height: "100%", 
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                  alt={attachment.fileName}
                  onError={(e) => {
                    e.target.style.display = "none";
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = "flex";
                    }
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    background: "transparent",
                    color: theme === "dark" ? "#90caf9" : "#1976d2",
                    width: 32,
                    height: 32,
                  }}
                >
                  {getFileIcon()}
                </Avatar>
              )}
            </Box>

            {/* File info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {editMode ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    value={newFileName}
                    onChange={(e) => setNewFileName(e.target.value)}
                    size="small"
                    variant="outlined"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveEdit();
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditMode(false);
                      setNewFileName(fileNameNoExt);
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              ) : (
                <>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {attachment.fileName}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {ext.toUpperCase()}
                    </Typography>
                    {attachment.size && (
                      <>
                        <Typography variant="caption" color="text.secondary">•</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(attachment.size)}
                        </Typography>
                      </>
                    )}
                    {kind === "client" && (
                      <>
                        <Typography variant="caption" color="text.secondary">•</Typography>
                        <Chip
                          label="Client"
                          size="small"
                          variant="outlined"
                          sx={{ 
                            height: 20, 
                            fontSize: "0.625rem",
                            color: theme === "dark" ? "#81c784" : "#388e3c",
                            borderColor: theme === "dark" ? "#81c784" : "#388e3c",
                          }}
                        />
                      </>
                    )}
                  </Box>
                </>
              )}
            </Box>

            {/* Actions */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                onClick={handleOpenNew}
                sx={{
                  color: theme === "dark" ? "#90caf9" : "#1976d2",
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
              
              {!readonly && (
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  sx={{
                    color: theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)",
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

AttachmentsV2.displayName = "AttachmentsV2";

export default AttachmentsV2;